import React, { type ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import { Box, Grid } from "@mui/material"
import { Button, InputText, NotificationUtils, Select, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormikContext } from "formik"
import { v4 as uuidv4 } from "uuid"

import { FeaturesMenu } from "../../../components/form/FeaturesMenu"
import { RadioButtonsCondition } from "../../../components/form/RadioButtonsCondition"
import { UploadFileBlock } from "../../../components/form/UploadFileBlock"
import { useDebounce } from "../../../hooks/useDebounce"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { UpdateWorkflowRequest } from "../../../types/custom/projects"
import { Operators } from "../../../types/custom/projects"
import type { NewFeatureData, Rule } from "../../../types/custom/rules"
import { MapValueTypeToFeatureType } from "../../../types/custom/rules"
import type { ConditionListFile } from "../../../types/generated/api/ConditionListFile"
import { DataFileGroup } from "../../../types/generated/api/DataFileGroup"
import type { DataFileGroupList } from "../../../types/generated/api/DataFileGroupList"
import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import { type StandardErrorType, withErrorHandler } from "../../../utils/ErrorHandling"
import { handleFeatureChange, multiValuesOperators, operatorsBasedOnFeatureType } from "../../../utils/conditionHelpers"
import { FILE_ID_MATCHER, isTypeIncluded, matchFile } from "../../../utils/rulesetHelpers"
import { ArrowAdornment } from "../../Scorecards/components/Condition"
import type { FeatureData } from "../../Workflows"
import { useWorkflowFeatures } from "../../Workflows/utils/workflowHelpers"
import {
  canRemoveCondition,
  getCondition,
  getErrorCondition,
  getFieldPath,
  getInitialNewFeatureType,
  getOpValuefromTranslation,
  getTouchedCondition,
  updateConditionValue,
} from "../helpers"
import type { ConditionProps, RuleCardFormikValues } from "../interfaces"
import { DataFilesMenu } from "./DataFilesMenu"
import { DataBlock } from "./RuleCard"

import styles from "./RuleCard.module.scss"

export function Condition(props: Readonly<ConditionProps>): React.ReactElement {
  const { editMode = false, createMode, id, index, removeCondition, lastSwitchSetter, ruleIndex, path } = props

  const { values, setFieldValue, setValues, handleChange, handleBlur, isSubmitting, errors, touched } =
    useFormikContext<RuleCardFormikValues>()

  // variable contains all features (schema, computed, constant)
  const allFeatures = useWorkflowFeatures(new Set(["SchemaFeatures", "ComputedFeatures", "ConstantFeatures"]))

  const workflowsList = useSelector((state: RootState) => state?.workflow?.workflows)

  const { t } = useTranslation("rulesets")

  // filtered features for LHS feature input
  const [filteredFeatures, setFilteredFeatures] = useState<Array<FeatureData>>(allFeatures ?? [])
  // filtered features for RHS feature input
  const [secondFilteredFeatures, setSecondFilteredFeatures] = useState<Array<FeatureData>>(allFeatures ?? [])
  // anchor for LHS feature
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  // anchor for RHS feature
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null)
  const [filesMenuAnchor, setFilesMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedFeat, setSelectedFeat] = useState<string>("")
  const [isFilteredFeaturesSet, setIsFilteredFeaturesSet] = useState(false)

  const [operators, setOperators] = useState<Operators[] | string[]>(
    createMode ? operatorsBasedOnFeatureType.TEXT : operatorsBasedOnFeatureType.UNDEFINED,
  )
  // ref for LHS feature
  const inputRef = useRef<HTMLInputElement>(null)
  // ref for RHS feature
  const RhsInputRef = useRef<HTMLInputElement>(null)
  const featuresMenuRef = useRef<HTMLInputElement>(null)

  const { id: projectID } = useParams<{ id: string }>()

  const abortControllerRef = useRef<AbortController | null>(null)

  const queryClient = useQueryClient()

  const [types, setTypes] = useState<Array<string>>(["string", "number", "true", "false"])
  const andOr = ["and", "or"]

  const condition = getCondition({ values, ruleIndex, path })

  // special handler for changing type field and all side effects in specific cases
  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), event.target.value)

    //When selecting true, false, or null from the menu -> set the value field to the selection
    if (isTypeIncluded(event?.target?.value)) {
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), event.target.value)
      setOperators([Operators["!="], Operators["="]])
    }

    // whenever number type is selected, change the operators to include all numerical operators
    if (event.target.value === "number") {
      setOperators(operatorsBasedOnFeatureType.NUMBER)
    }

    if (event.target.value === "string") {
      setOperators(operatorsBasedOnFeatureType.TEXT)
    }

    //When selecting number or string from the menu -> reset the value field
    else if (event.target.value === "string" || event.target.value === "number") {
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "")
    }
  }

  // special handler for changing operator field and all side effects in specific cases
  const handleOperatorChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (["is null", "not null"].includes(event.target.value)) {
      setFieldValue(getFieldPath({ field: "operator", ruleIndex, path }), event.target.value)

      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "null")
      setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "null")
      return
    }

    if (
      !["is null", "not null"].includes(event.target.value) &&
      ["is null", "not null"].includes(condition?.operator ?? "is null")
    ) {
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "")
      setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "string")
    }

    setFieldValue(getFieldPath({ field: "operator", ruleIndex, path }), event.target.value)

    // reset the value field when selecting in/not in operators from the menu
    if (![Operators["!="], Operators["="]].includes(Operators[event.target.value as keyof typeof Operators])) {
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "")
    }

    if (
      [Operators.between, ...multiValuesOperators].includes(Operators[event.target.value as keyof typeof Operators])
    ) {
      setFieldValue(getFieldPath({ field: "valueOrFeature", ruleIndex, path }), "Value")
    }

    // reset the value field when selecting not equal/equal operators and there is an uploaded file
    if (
      matchFile(condition?.value ?? "").isMatching &&
      (event.target.value === Operators["!="] || condition?.operator === Operators["="])
    ) {
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "")
    }

    // automatically switch value's type to number when these operators are active
    // -> "greater than", "greater than/equal", "lower than", "lower than/equal", "between"
    if (
      ["greater than", "greater than/equal", "lower than", "lower than/equal", "between"].includes(event.target.value)
    ) {
      setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "number")
    }

    // automatically switch value's type to string when these operators are active
    // -> "in", "not in", "contains", "not contains"
    if (multiValuesOperators.includes(event.target.value)) {
      setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "string")
    }
  }

  // update a workflow
  const updateWorkflowMutation = useMutation<AxiosResponse, AxiosError, UpdateWorkflowRequest>(
    KonanAPI.updateWorkflow,
    {
      onSuccess: () => {
        NotificationUtils.toast(t("notifications.featureAdded"), {
          severity: "success",
        })
        queryClient.invalidateQueries("workflows")
        queryClient.invalidateQueries("workflow")
        queryClient.invalidateQueries(["project", projectID as string])

        setFilteredFeatures(
          allFeatures.filter((item: FeatureData) => condition?.feature && item?.name.includes(condition?.feature)),
        )
        setSecondFilteredFeatures(
          allFeatures.filter(
            (item: FeatureData) => condition?.secondFeature && item?.name.includes(condition?.secondFeature),
          ),
        )

        setIsFilteredFeaturesSet(false)
      },

      onError: () => {
        NotificationUtils.toast(t("notifications.featureAddError"), {
          severity: "error",
        })
      },
    },
  )

  const handleAddDataFile = (file: DataFileGroupList): void => {
    setValues((prev) => {
      if (!prev.cards[ruleIndex]) return prev

      prev.cards[ruleIndex].condition_list_files = [
        ...prev.cards[ruleIndex].condition_list_files,
        { uuid: file.uuid, name: file.name, version: "" },
      ]

      // Update the condition value based on the new groups structure
      const condition = getCondition({ values: prev, ruleIndex, path })
      if (condition) {
        condition.value = `$file<${file.uuid}>`
      }

      return { ...prev }
    })
  }

  const {
    isLoading: isCsvUploading,
    mutateAsync: uploadCSVAsyncMutation,
    reset,
  } = useMutation<DataFileGroup, AxiosError<StandardErrorType>, File>(
    (file: File) => {
      abortControllerRef.current = new AbortController()
      return KonanAPI.uploadDataFile({
        project_uuid: projectID as string,
        file: file,
        signal: abortControllerRef.current.signal,
        type: DataFileGroup.type.CONDITION_LIST,
      })
    },
    {
      mutationKey: "uploadCSV",
      onSuccess: (data) => handleAddDataFile(data),
      onError: withErrorHandler(() => {
        if (condition) condition.value = ""
      }),
    },
  )

  // handle close dropzone
  const handleCloseAndAbort = useCallback(() => {
    abortControllerRef.current?.abort()
    reset()
  }, [reset])

  // handle upload once files are accepted
  const handleUpload = async (file: File): Promise<void> => {
    try {
      await uploadCSVAsyncMutation(file)

      // Invalidate training data to trigger a refresh
      await queryClient.invalidateQueries(["data-files", projectID])
    } catch {
      // reset dropzone on error
      handleCloseAndAbort()
    }
  }

  // handle delete data file
  const handleDeleteFile = async (): Promise<void> => {
    setValues((prev) => {
      const condition = getCondition({ values: prev, ruleIndex, path })
      if (!condition) return prev

      const conditionValue = condition.value

      if (prev.cards[ruleIndex]) {
        prev.cards[ruleIndex].condition_list_files = prev.cards[ruleIndex].condition_list_files.filter(
          (file: { uuid: string; name: string }) => file.uuid !== conditionValue.match(FILE_ID_MATCHER)?.[1],
        ) as ConditionListFile[]
      }

      // Update the condition value using recursive helper
      return updateConditionValue({ values: prev, ruleIndex, path, newValue: "" })
    })
  }

  const currentFeature = useMemo(() => {
    const feature = condition?.feature
    if (filteredFeatures?.length > 0 && feature) {
      const current = filteredFeatures?.find((feat) => feat?.name === feature)
      return current
    }
  }, [condition?.feature, filteredFeatures])

  const handleUpdateWorkflow = async (featureData: NewFeatureData, featureName: string): Promise<void> => {
    // Fetch single workflow
    const { data: workflowData } = await queryClient.fetchQuery(["workflow", projectID, featureData.workflow], () =>
      KonanAPI.fetchWorkflow(projectID as string, featureData.workflow),
    )

    const newFeat = {
      name: featureName?.replace(/\s/g, ""),
      type: featureData.type,
      is_required: featureData.isRequired,
      id: uuidv4(),
      source: "workflow",
      new: true,
    }

    if (workflowData?.active_version) {
      await updateWorkflowMutation.mutateAsync({
        name: workflowData?.name,
        projectId: projectID as string,
        workflowUUID: workflowData?.uuid,
        schema: [...(workflowData?.active_version?.schema?.features ?? []), newFeat],
      })
    }
  }

  const debouncedOnChangeLHS = useDebounce((): void => {
    const updatedConditionParts = handleFeatureChange(allFeatures, condition?.feature as string, condition as Rule)
    setFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
    setSecondFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
    setOperators(updatedConditionParts?.operators)
    setFieldValue(id + ".operator", updatedConditionParts?.operator)

    setTypes(updatedConditionParts?.types)

    setFieldValue(id + ".type", updatedConditionParts?.type)

    setFieldValue(id + ".value", updatedConditionParts?.value)

    setAnchorEl(inputRef?.current)
    setTimeout(() => {
      inputRef?.current?.focus()
    }, 100)
  }, 400)

  const debouncedOnChangeRHS = useDebounce((): void => {
    const updatedConditionParts = handleFeatureChange(
      allFeatures,
      condition?.secondFeature as string,
      condition as Rule,
    )
    setFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
    setSecondFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
    setOperators(updatedConditionParts?.operators)
    setFieldValue(id + ".operator", updatedConditionParts?.operator)

    setTypes(updatedConditionParts?.types)

    setFieldValue(id + ".type", updatedConditionParts?.type)

    setFieldValue(id + ".value", updatedConditionParts?.value)

    setAnchorEl2(RhsInputRef?.current)

    setTimeout(() => {
      RhsInputRef?.current?.focus()
    }, 100)
  }, 400)

  const shouldDisplayFileBlock = Boolean(
    (isCsvUploading || matchFile(condition?.value as string).isMatching) &&
      multiValuesOperators.includes(condition?.operator as string) &&
      (editMode || createMode),
  )

  const handleFeatureSelectionFromMenu = (item: FeatureData, featureIndicator: "LHS" | "RHS"): void => {
    setSelectedFeat(item?.name)
    const updatedConditionParts = handleFeatureChange(allFeatures, item?.name, condition as Rule)
    setFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
    setSecondFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
    setOperators(updatedConditionParts?.operators)
    setFieldValue(id + ".operator", updatedConditionParts?.operator)

    setTypes(updatedConditionParts?.types)

    setFieldValue(id + ".type", updatedConditionParts?.type)

    setFieldValue(id + ".value", updatedConditionParts?.value)

    if (featureIndicator === "LHS") {
      setFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
      setSecondFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
    } else {
      setFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
      setSecondFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
    }
  }

  /**
   * indicator flag for displaying value/feature options (radio buttons options)
   */
  const shouldDisplayValueOrFeature =
    // 1. when ruleset in edit/create mode
    (createMode || editMode) &&
    // 2. if operator is not in [in, not in, contains, not contains]
    !["is null", "not null", "between"].includes(condition?.operator as string) &&
    // 3. if value type isn't in [true, false, null] OR equal to any of these values
    // but feature is empty or currentFeature not equal to boolean or undefined
    (!isTypeIncluded(condition?.type as string) ||
      (isTypeIncluded(condition?.type as string) &&
        currentFeature &&
        currentFeature?.type !== WorkflowInputFeature.type.BOOLEAN &&
        currentFeature?.type !== WorkflowInputFeature.type.UNDEFINED))

  const handleFeatureTypeChange = (value: string): void => {
    switch (value) {
      case WorkflowInputFeature.type.TEXT:
      case WorkflowInputFeature.type.UNDEFINED:
      case WorkflowInputFeature.type.DATE:
        if (
          (condition?.type === "true" && condition?.value === "true") ||
          (condition?.type === "false" && condition?.value === "false")
        ) {
          setFieldValue(id + ".value", "")
        }

        setFieldValue(id + ".type", "string")
        setOperators(operatorsBasedOnFeatureType.TEXT)
        setFieldValue(id + ".operator", "equal")
        break

      case WorkflowInputFeature.type.BOOLEAN:
        setFieldValue(id + ".type", "true")
        setFieldValue(id + ".value", "true")
        setOperators([Operators["!="], Operators["="]])
        setFieldValue(id + ".operator", "equal")
        break

      case WorkflowInputFeature.type.NUMBER:
        if (
          (condition?.type === "true" && condition?.value === "true") ||
          (condition?.type === "false" && condition?.value === "false")
        ) {
          setFieldValue(id + ".value", "")
        }

        setFieldValue(id + ".type", "number")
        setOperators(Object.values(Operators).filter((v) => isNaN(Number(v))))
        setFieldValue(id + ".operator", "equal")
        break

      case WorkflowInputFeature.type.LIST:
        setFieldValue(id + ".operator", "in")

        // reset the value field when selecting in/not in operators from the menu
        setFieldValue(id + ".value", "")
        setFieldValue(id + ".valueOrFeature", "Value")
        break

      default:
        break
    }
  }

  useEffect(() => {
    if (!isFilteredFeaturesSet) {
      setFilteredFeatures(allFeatures)
      setSecondFilteredFeatures(allFeatures)
      if (allFeatures?.length > 0) {
        setIsFilteredFeaturesSet(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilteredFeaturesSet])

  return (
    <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
      {!!values?.cards?.[ruleIndex]?.conditions && (
        <Fragment>
          {/* And/Or */}
          {index !== 0 ? (
            <Grid item lg={1.25}>
              {editMode || createMode ? (
                <Select
                  id={id + ".andOr"}
                  type="text"
                  value={getCondition({ values, ruleIndex, path })?.andOr ?? "and"}
                  // disabled={index > 1}
                  handleBlur={handleBlur}
                  handleChange={(e) => {
                    handleChange(e)
                    if (lastSwitchSetter) {
                      setFieldValue(getFieldPath({ field: "andOr", ruleIndex, path }), e.target.value)
                    }
                  }}
                  fullWidth
                  options={andOr}
                  hideDescription
                  menuProps={{ menuMaxContent: true }}
                />
              ) : (
                <DataBlock value={getCondition({ values, ruleIndex, path })?.andOr ?? "and"} />
              )}
            </Grid>
          ) : (
            /* If */
            <Grid item lg={1}>
              <Box display="flex" justifyContent="flex-end">
                <Typography variant="p">{t("if")}</Typography>
              </Box>
            </Grid>
          )}

          {/* Feature */}
          <Grid item xs={6} md={editMode || createMode ? 3 : true}>
            {editMode || createMode ? (
              <div>
                <InputText
                  key={`${selectedFeat}_${values?.cards?.length}`}
                  hideDescription
                  id={id + ".feature"}
                  placeholder={t("inputFeaturePlaceHolder")}
                  value={condition?.feature}
                  anchoringRef={inputRef}
                  endAdornment={
                    allFeatures.length > 0 ? (
                      <ArrowAdornment
                        anchorEl={anchorEl}
                        onClick={() => setAnchorEl(!!anchorEl ? null : inputRef?.current)}
                      />
                    ) : undefined
                  }
                  handleChange={(e) => {
                    handleChange(e)
                    setAnchorEl(null)
                    debouncedOnChangeLHS()
                  }}
                  error={
                    getTouchedCondition({ touched, ruleIndex, path })?.feature &&
                    getErrorCondition({ errors, ruleIndex, path })?.feature
                  }
                  handleBlur={handleBlur}
                  disabled={isSubmitting}
                  fullWidth
                />
                <FeaturesMenu
                  handleFeatureTypeChange={(value: string) => handleFeatureTypeChange(value)}
                  initialFeatureType={() => getInitialNewFeatureType(condition)}
                  filteredFeatures={filteredFeatures}
                  workflowsList={workflowsList}
                  anchorEl={anchorEl}
                  queryKey={condition?.feature as string}
                  onAddingNewFeature={(featureData) => {
                    setAnchorEl(null)
                    handleUpdateWorkflow(featureData, condition?.feature as string)
                  }}
                  onSelectingFeature={(item) => {
                    setFieldValue(id + ".feature", item?.name)
                    handleFeatureSelectionFromMenu(item, "LHS")
                    setAnchorEl(null)
                  }}
                  onMenuClose={() => setAnchorEl(null)}
                />
              </div>
            ) : (
              <DataBlock value={condition?.feature} />
            )}
          </Grid>

          {/* Operator */}
          {/* TODO: add operator size check */}
          <Grid item xs md={["is null", "not null"].includes(condition?.operator as string) ? true : 2.4}>
            {editMode || createMode ? (
              <Select
                id={id + ".operator"}
                key={condition?.id as string}
                type="text"
                fullWidth
                hideDescription
                handleBlur={handleBlur}
                value={t(`operators.${condition?.operator}`)}
                handleChange={(e) => {
                  e.target.value = getOpValuefromTranslation({
                    translation: e.target.value as string,
                    t,
                  }) as string
                  handleOperatorChange(e as ChangeEvent<HTMLInputElement>)
                }}
                options={operators.map((op) => t(`operators.${op}`))}
                menuProps={{ menuMaxContent: true }}
              />
            ) : (
              <DataBlock value={t(`operators.${condition?.operator}`)} />
            )}
          </Grid>

          {/* Type */}
          {((!currentFeature && !isSubmitting) ||
            currentFeature?.type === "UNDEFINED" ||
            isTypeIncluded(condition?.type as string) ||
            currentFeature?.type === "BOOLEAN") &&
            condition?.valueOrFeature !== "Feature" &&
            !["is null", "not null"].includes(condition?.operator as string) &&
            (editMode || createMode) && (
              <Grid
                item
                xs
                lg={isTypeIncluded(condition?.type as string) ? true : 1.5}
                style={
                  ((editMode || createMode) &&
                    condition?.operator !== Operators["!="] &&
                    condition?.operator !== Operators["="]) ||
                  (!editMode &&
                    !createMode &&
                    (multiValuesOperators.includes(Operators[condition?.operator as keyof typeof Operators]) ||
                      isTypeIncluded(condition?.type as string)))
                    ? { display: "none" }
                    : undefined
                }
              >
                {editMode || createMode ? (
                  <Select
                    id={id + ".type"}
                    key={condition?.id as string}
                    type="text"
                    handleBlur={handleBlur}
                    value={condition?.type}
                    handleChange={(e) => handleTypeChange(e as ChangeEvent<HTMLInputElement>)}
                    fullWidth
                    options={types}
                    hideDescription
                    menuProps={{ menuMaxContent: true }}
                  />
                ) : (
                  <DataBlock value={condition?.type} />
                )}
              </Grid>
            )}

          {/* Feature/value radio buttons*/}
          {shouldDisplayValueOrFeature && (
            <RadioButtonsCondition
              handleOnChange={(e) => {
                setFieldValue(id + ".valueOrFeature", e.target.value)

                setOperators(
                  operatorsBasedOnFeatureType[
                    MapValueTypeToFeatureType[condition?.type as keyof typeof MapValueTypeToFeatureType]
                  ],
                )
              }}
              checkedFeat={condition?.valueOrFeature === "Feature"}
              checkedValue={condition?.valueOrFeature === "Value"}
              shouldDisableFeature={matchFile(condition?.value as string).isMatching}
              margin={"0.5rem 0.12rem 0px 0.4rem"}
            />
          )}

          {/* Value */}
          {condition?.operator?.toLowerCase() === Operators.between.toLowerCase() ? (
            <Grid flexWrap={"nowrap"} xs item container key={condition?.id}>
              <Grid item xs md key={condition?.id}>
                {editMode || createMode ? (
                  <InputText
                    id={id + ".value"}
                    hideDescription
                    key={condition?.id as string}
                    placeholder={t("valueInputPlaceholder")}
                    value={condition?.value}
                    handleChange={handleChange}
                    type="number"
                    error={
                      getTouchedCondition({ touched, ruleIndex, path })?.value &&
                      getErrorCondition({ errors, ruleIndex, path })?.value
                    }
                    handleBlur={handleBlur}
                    disabled={isSubmitting}
                    fullWidth
                  />
                ) : (
                  <DataBlock value={condition?.value} />
                )}
              </Grid>
              <Grid
                item
                xs={0.2}
                minWidth={"50px"}
                mt={!editMode && !createMode ? 0 : 0.8}
                ml={1}
                mr={1}
                className="and-block"
              >
                <Typography variant="h3-bold" color="base">
                  {t("and")}
                </Typography>
              </Grid>
              <Grid item xs md>
                {editMode || createMode ? (
                  <InputText
                    id={id + ".secondValue"}
                    hideDescription
                    placeholder={t("valueInputPlaceholder")}
                    value={condition?.secondValue}
                    key={condition?.id as string}
                    handleChange={handleChange}
                    type="number"
                    error={
                      getTouchedCondition({ touched, ruleIndex, path })?.secondValue &&
                      getErrorCondition({ errors, ruleIndex, path })?.secondValue
                    }
                    handleBlur={handleBlur}
                    disabled={isSubmitting}
                    fullWidth
                  />
                ) : (
                  <DataBlock value={condition?.secondValue} />
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid
              item
              xs
              key={condition?.id}
              // Important: hide this field using display: "none" in order NOT to lose access to this element by id,
              // as the id of this field is used by the formik object
              style={
                ((editMode || createMode) && isTypeIncluded(condition?.type as string)) ||
                ["is null", "not null"].includes(condition?.operator as string)
                  ? { display: "none" }
                  : undefined
              }
            >
              {shouldDisplayFileBlock ? (
                <UploadFileBlock
                  onDelete={handleDeleteFile}
                  fileName={
                    values?.cards?.[ruleIndex]?.condition_list_files?.find(
                      (file: { uuid: string; name: string }) =>
                        file.uuid === matchFile(condition?.value as string).fileId,
                    )?.name ?? ""
                  }
                  isUploading={isCsvUploading}
                  handleCancel={handleCloseAndAbort}
                  isReadMode={!editMode && !createMode}
                  fileID={matchFile(condition?.value as string).fileId}
                />
              ) : (editMode || createMode) && condition?.valueOrFeature === "Feature" ? (
                <div>
                  <InputText
                    key={`${selectedFeat}_${values?.cards?.length}`}
                    id={id + ".secondFeature"}
                    hideDescription
                    placeholder={t("inputFeaturePlaceHolder")}
                    value={condition?.secondFeature}
                    anchoringRef={RhsInputRef}
                    endAdornment={
                      allFeatures.length > 0 ? (
                        <ArrowAdornment
                          anchorEl={anchorEl2}
                          onClick={() => setAnchorEl2(!!anchorEl2 ? null : RhsInputRef?.current)}
                        />
                      ) : undefined
                    }
                    handleChange={(e) => {
                      handleChange(e)
                      setAnchorEl2(null)
                      debouncedOnChangeRHS()
                    }}
                    error={
                      getTouchedCondition({ touched, ruleIndex, path })?.secondFeature &&
                      getErrorCondition({ errors, ruleIndex, path })?.secondFeature
                    }
                    handleBlur={handleBlur}
                    disabled={isSubmitting}
                    fullWidth
                  />
                  <FeaturesMenu
                    filteredFeatures={secondFilteredFeatures}
                    workflowsList={workflowsList}
                    anchorEl={anchorEl2}
                    queryKey={condition?.secondFeature as string}
                    onAddingNewFeature={(featureData) => {
                      setAnchorEl2(null)
                      handleUpdateWorkflow(featureData, condition?.secondFeature as string)
                    }}
                    onSelectingFeature={(item) => {
                      setFieldValue(id + ".secondFeature", item?.name)
                      handleFeatureSelectionFromMenu(item, "RHS")
                      setAnchorEl2(null)
                    }}
                    onMenuClose={() => setAnchorEl2(null)}
                  />
                </div>
              ) : editMode || createMode ? (
                <Fragment>
                  <InputText
                    id={id + ".value"}
                    placeholder={t("valueInputPlaceholder")}
                    value={condition?.value}
                    anchoringRef={featuresMenuRef}
                    hideDescription
                    key={condition?.id as string}
                    endAdornment={
                      multiValuesOperators.includes(Operators[condition?.operator as keyof typeof Operators]) ? (
                        <ArrowAdornment
                          anchorEl={filesMenuAnchor}
                          onClick={() => setFilesMenuAnchor(!!filesMenuAnchor ? null : featuresMenuRef?.current)}
                          style={{ color: "var(--base-text-2)" }}
                        />
                      ) : undefined
                    }
                    handleChange={handleChange}
                    error={
                      getTouchedCondition({ touched, ruleIndex, path })?.value &&
                      getErrorCondition({ errors, ruleIndex, path })?.value
                    }
                    type={
                      condition?.type !== "string" &&
                      !multiValuesOperators.includes(Operators[condition?.operator as keyof typeof Operators])
                        ? "number"
                        : "text"
                    }
                    handleBlur={handleBlur}
                    disabled={isSubmitting}
                    fullWidth
                    description={
                      [Operators["in"], Operators["not in"]].includes(
                        Operators[condition?.operator as keyof typeof Operators],
                      )
                        ? "Use single quotes for strings. Example: 'john', 'alex'"
                        : [Operators["contains"], Operators["not contains"]].includes(
                              Operators[condition?.operator as keyof typeof Operators],
                            )
                          ? "Use single quotes for strings. Example: 'john', 'alex', or enter one string"
                          : ""
                    }
                  />
                  {filesMenuAnchor && (
                    <DataFilesMenu
                      onDataFileUpload={handleUpload}
                      onAddDataFile={handleAddDataFile}
                      anchorEl={filesMenuAnchor}
                      onClose={() => setFilesMenuAnchor(null)}
                    />
                  )}
                </Fragment>
              ) : (
                <div className={styles.valueFieldMargin}>
                  <DataBlock
                    value={
                      matchFile(condition?.value as string).isMatching
                        ? (values?.cards?.[ruleIndex]?.condition_list_files?.find(
                            (file: { uuid: string; name: string }) =>
                              file.uuid === matchFile(condition?.value as string).fileId,
                          )?.name ?? "")
                        : condition?.value
                    }
                    isLoading={false}
                    fileId={
                      matchFile(condition?.value as string).isMatching
                        ? matchFile(condition?.value as string).fileId
                        : undefined
                    }
                  />
                </div>
              )}
            </Grid>
          )}

          {/* Remove button */}
          {(editMode || createMode) && canRemoveCondition({ values, ruleIndex, path }) && (
            <Grid item>
              <Button
                variant="dangerous"
                size="small"
                onClick={() => {
                  removeCondition?.(
                    !!(
                      multiValuesOperators.includes(Operators[condition?.operator as keyof typeof Operators]) &&
                      matchFile(condition?.value as string).isMatching
                    ),
                  )
                }}
              >
                <RemoveCircleOutlineIcon fontSize="inherit" />
              </Button>
            </Grid>
          )}
        </Fragment>
      )}
    </Grid>
  )
}
